import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useGroupProjects } from "@/hooks/use-group-projects";
import { useGroupRunners } from "@/hooks/use-group-runners";
import { useDeleteGroup } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group } from "gitpod-next-api/gitpod/v1/group_pb";
import { useCallback, type FC } from "react";
import { ProjectsSection } from "@/components/settings/organization/groups/ProjectsSection";
import { RunnersSection } from "@/components/settings/organization/groups/RunnersSection";

export type DeleteGroupDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    group: PlainMessage<Group>;
    onSuccess?: () => void;
};

export const DeleteGroupDialog: FC<DeleteGroupDialogProps> = ({ open, onOpenChange, group, onSuccess }) => {
    const { toast } = useToast();
    const deleteGroup = useDeleteGroup();
    const { projects, isLoading: isLoadingProjects } = useGroupProjects(group.id);
    const { runners, isLoading: isLoadingRunners } = useGroupRunners(group.id);

    const hasResources = projects.length > 0 || runners.length > 0;
    const isLoadingResources = isLoadingProjects || isLoadingRunners;

    const handleConfirm = useCallback(async () => {
        try {
            await deleteGroup.mutateAsync(group.id);
            toast({
                title: "Group deleted",
                description: `${group.name} has been deleted successfully.`,
            });
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            toast({
                title: "Failed to delete group",
                description: formatError(error),
            });
        }
    }, [deleteGroup, group.id, group.name, toast, onOpenChange, onSuccess]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.SettingsGroupDelete} className="max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        <span className="truncate">Are you sure you want to delete the {group.name} group?</span>
                    </DialogTitle>
                    <DialogDescription>
                        {hasResources || isLoadingResources
                            ? "This will permanently remove the group and revoke member access to these resources:"
                            : "This will permanently remove the group."}
                    </DialogDescription>
                </DialogHeader>

                {(hasResources || isLoadingResources) && (
                    <div className="space-y-4 rounded-lg border border-border-base p-2">
                        <ProjectsSection groupId={group.id} />
                        <RunnersSection groupId={group.id} />
                    </div>
                )}

                {!hasResources && !isLoadingResources && (
                    <div className="text-muted-foreground rounded-lg border border-border-base bg-surface-secondary p-4 text-center text-sm">
                        This group has no resource assignments.
                    </div>
                )}

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={deleteGroup.isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        loading={deleteGroup.isPending}
                        disabled={deleteGroup.isPending}
                        data-tracking-id="delete"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
